import Container from "../Container";
import SectionHeader from "../SectionHeader";
import { attentionsItem, attentionsItemMob } from "views/landing/constants";
import AttentionCard from "./components/AttentionCard";
import { DotLottiePlayer } from "@dotlottie/react-player";
import logo from "../../../../assets/landing/animation-middle-logo.png";
import { useEffect, useRef } from "react";
import { useAnimation, useInView, motion } from "framer-motion";
import { useMediaQuery } from "usehooks-ts";

const AttentionFlywheel = () => {
  const matches = useMediaQuery("(min-width: 1024px)");
  const currentValues = matches ? attentionsItem : attentionsItemMob;

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  // Parent container animation d
  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 1, ease: "easeOut", staggerChildren: 0.2 },
    },
  };

  const attentionContainerVariants = {
    hidden: { opacity: 1 }, // don’t fade the grid, we just want to orchestrate children
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        delayChildren: 0.15,
        staggerChildren: 0.35,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const flywheelVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    show: { opacity: 1, scale: 1, transition: { duration: 0.5 } },
  };
  const mobileFlywheelVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    show: { opacity: 1, scale: 1, transition: { duration: 0.5, delay: 0.2 } }, // Example delay
  };

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={containerVariants}
    >
      <Container
        animationControls={controls}
        sectionClasses="mb-[9vw] max-lg:mb-[25vw]"
      >
        <SectionHeader
          title="AGNT Ecosystem Flywheel"
          desc="How users interact with AGNT"
          sectionHeaderClasses="mb-[6vw]"
        />
        <motion.div className="max-lg:relative max-lg:grid max-lg:grid-cols-[16vw_1fr] max-lg:gap-[4.23vw]">
          <motion.div
            className="lg:hidden w-full overflow-hidden relative"
            variants={mobileFlywheelVariants}
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
          >
            <DotLottiePlayer
              className="w-full top-[1.5vw] absolute"
              src="/FlyWheelMobile4.json"
              style={{
                transform: "scale(1.02)",
                transformOrigin: "center",
              }}
              autoplay
              loop
            />
          </motion.div>

          <motion.div
            key={matches ? "lg" : "sm"}
            className="grid relative grid-cols-2 gap-y-[5.8vw] gap-x-[23.5vw] max-lg:grid-cols-1 max-lg:gap-[5.33vw]"
            variants={attentionContainerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            {currentValues.map(({ items, title, id }, index) => (
              <motion.div variants={cardVariants} key={id}>
                <div className="lg:justify-self-center">
                  <AttentionCard
                    title={title}
                    items={items}
                    isOdd={(index + 1) % 2 === 1}
                    currentIndex={index}
                  />
                </div>
              </motion.div>
            ))}

            {/* Desktop Flywheel unchanged */}
            <div className="absolute max-lg:hidden left-1/2 -translate-x-1/2 -top-[7.5vw]">
              <motion.div
                variants={flywheelVariants}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
              >
                <DotLottiePlayer
                  className="size-[37.5vw]"
                  src="/FlyWheel3.json"
                  autoplay
                  loop
                />
                <img
                  className="absolute size-[8.714vw] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                  src={logo}
                  alt="Tree circles connecting in triangle and with different colors"
                />
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </Container>
    </motion.div>
  );
};

export default AttentionFlywheel;
